import { __decorate } from "tslib";
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';
import { ifNonEmpty, ifNumber } from '../../base/directives';
import { vdsEvent } from '../../base/events';
import { ViewType } from '../../media';
import { Html5MediaElement } from '../html5';
import { VideoFullscreenController } from './fullscreen';
import { VideoPresentationController } from './presentation';
import { videoElementStyles } from './styles';
export const VIDEO_ELEMENT_TAG_NAME = 'vds-video';
/**
 * Embeds video content into documents via the native `<video>` element. It may contain
 * one or more video sources, represented using the `src` attribute or the `<source>` element: the
 * browser will choose the most suitable one.
 *
 * The list of [supported media formats](https://developer.mozilla.org/en-US/docs/Web/Media/Formats)
 * varies from one browser to the other. You should either provide your video in a single format
 * that all the relevant browsers support, or provide multiple video sources in enough different
 * formats that all the browsers you need to support are covered.
 *
 * 💡 This element contains the exact same interface as the `<video>` element. It redispatches
 * all the native events if needed, but prefer the `vds-*` variants (eg: `vds-play`) as they
 * iron out any browser issues.
 *
 * @tagname vds-video
 * @slot Used to pass in `<source>` and `<track>` elements to the underlying HTML5 media player.
 * @csspart media - The video element (`<video>`).
 * @csspart video - Alias for `media` part.
 * @example
 * ```html
 * <vds-video src="/media/video.mp4" poster="/media/poster.png">
 *   <!-- Additional media resources here. -->
 * </vds-video>
 * ```
 * @example
 * ```html
 * <vds-video poster="/media/poster.png">
 *   <source src="/media/video.mp4" type="video/mp4" />
 *   <track default kind="subtitles" src="/media/subs/en.vtt" srclang="en" label="English" />
 * </vds-video>
 * ```
 */
export class VideoElement extends Html5MediaElement {
    constructor() {
        super(...arguments);
        // -------------------------------------------------------------------------------------------
        // Fullscreen
        // -------------------------------------------------------------------------------------------
        this.presentationController = new VideoPresentationController(this);
        this.fullscreenController = new VideoFullscreenController(this, this.screenOrientationController, this.presentationController);
    }
    static get styles() {
        return [videoElementStyles];
    }
    static get parts() {
        return ['root', 'video'];
    }
    /**
     * A URL for an image to be shown while the video is downloading. If this attribute isn't
     * specified, nothing is displayed until the first frame is available, then the first frame is
     * shown as the poster frame.
     */
    get poster() {
        return this.ctx.currentPoster;
    }
    set poster(newPoster) {
        this._connectedQueue.queue('currentPoster', () => {
            this.ctx.currentPoster = newPoster;
            this.requestUpdate();
        });
    }
    get mediaElement() {
        return this._mediaRef.value;
    }
    get videoElement() {
        return this.mediaElement;
    }
    get engine() {
        return this.mediaElement;
    }
    get videoEngine() {
        return this.videoElement;
    }
    // -------------------------------------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------------------------------------
    connectedCallback() {
        super.connectedCallback();
        this.ctx.viewType = ViewType.Video;
        this.dispatchEvent(vdsEvent('vds-view-type-change', {
            detail: ViewType.Video
        }));
    }
    // -------------------------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------------------------
    render() {
        return this._renderVideo();
    }
    _renderVideo() {
        var _a, _b;
        return html `
      <video
        part="${this._getVideoPartAttr()}"
        src="${ifNonEmpty(this._shouldSetVideoSrcAttr() ? this.src : '')}"
        width="${ifNumber(this.width)}"
        height="${ifNumber(this.height)}"
        poster="${ifNonEmpty(this.poster)}"
        preload="${ifNonEmpty(this.preload)}"
        crossorigin="${ifNonEmpty(this.crossOrigin)}"
        controlslist="${ifNonEmpty(this.controlsList)}"
        ?playsinline="${this.playsinline}"
        ?controls="${this.controls}"
        ?autopictureinpicture="${this.autoPiP}"
        ?disablepictureinpicture="${this.disablePiP}"
        ?disableremoteplayback="${this.disableRemotePlayback}"
        .defaultMuted="${(_a = this.defaultMuted) !== null && _a !== void 0 ? _a : this.muted}"
        .defaultPlaybackRate="${(_b = this.defaultPlaybackRate) !== null && _b !== void 0 ? _b : 1}"
        ${ref(this._mediaRef)}
      >
        ${this._renderMediaChildren()}
      </video>
    `;
    }
    /**
     * Override this to modify video CSS Parts.
     */
    _getVideoPartAttr() {
        return 'media video';
    }
    /**
     * Can be used by attaching engine such as `hls.js` to prevent src attr being set on
     * `<video>` element.
     */
    _shouldSetVideoSrcAttr() {
        return true;
    }
    // -------------------------------------------------------------------------------------------
    // Methods
    // -------------------------------------------------------------------------------------------
    /**
     * Issues an asynchronous request to display the video in picture-in-picture mode.
     *
     * It's not guaranteed that the video will be put into picture-in-picture. If permission to enter
     * that mode is granted, the returned `Promise` will resolve and the video will receive a
     * `enterpictureinpicture` event to let it know that it's now in picture-in-picture.
     *
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLVideoElement/requestPictureInPicture
     */
    async requestPictureInPicture() {
        var _a;
        return (_a = this.videoElement) === null || _a === void 0 ? void 0 : _a.requestPictureInPicture();
    }
}
__decorate([
    property({ type: Boolean, attribute: 'autopictureinpicture' })
], VideoElement.prototype, "autoPiP", void 0);
__decorate([
    property({ type: Boolean, attribute: 'disablepictureinpicture' })
], VideoElement.prototype, "disablePiP", void 0);
__decorate([
    property()
], VideoElement.prototype, "poster", null);
